import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle } from 'lucide-react';
import {
  useCreateButtonAction,
  useUpdateButtonAction,
  type ButtonAction,
  type ButtonType,
  type ButtonLocation
} from '../../../services/queries/buttonActionQueries';
import { ActionConfigurator } from './ActionConfigurator';
import { VisibilityRulesBuilder } from './VisibilityRulesBuilder';
import { useToast } from '../../../contexts/ToastContext';

interface ButtonActionBuilderProps {
  button?: ButtonAction;
  objectName: string;
  objectLabel: string;
  allFields: any[];
  onClose: () => void;
}

export const ButtonActionBuilder: React.FC<ButtonActionBuilderProps> = ({
  button: initialButton,
  objectName,
  objectLabel,
  allFields,
  onClose
}) => {
  const [buttonData, setButtonData] = useState<Partial<ButtonAction>>({
    name: initialButton?.name || '',
    objectName,
    type: initialButton?.type || 'button',
    location: initialButton?.location || 'detail-header',
    label: initialButton?.label || '',
    icon: initialButton?.icon || '',
    color: initialButton?.color || 'purple',
    action: initialButton?.action || { type: 'call-api' },
    successMessage: initialButton?.successMessage || 'Action completed successfully!',
    errorMessage: initialButton?.errorMessage || 'Action failed. Please try again.',
    visibilityRules: initialButton?.visibilityRules || [],
    visibilityLogic: initialButton?.visibilityLogic || 'AND',
    isActive: initialButton?.isActive ?? true
  });

  const [hasChanges, setHasChanges] = useState(false);

  const createButtonMutation = useCreateButtonAction();
  const updateButtonMutation = useUpdateButtonAction();
  const { success, error } = useToast();

  useEffect(() => {
    if (initialButton) {
      setButtonData({
        name: initialButton.name,
        objectName: initialButton.objectName,
        type: initialButton.type,
        location: initialButton.location,
        label: initialButton.label,
        icon: initialButton.icon,
        color: initialButton.color,
        action: initialButton.action,
        successMessage: initialButton.successMessage,
        errorMessage: initialButton.errorMessage,
        visibilityRules: initialButton.visibilityRules,
        visibilityLogic: initialButton.visibilityLogic,
        isActive: initialButton.isActive
      });
    }
  }, [initialButton]);

  const handleSave = async () => {
    // Validation
    if (!buttonData.name) {
      error('Button name required', 'Please enter a button name');
      return;
    }

    if (!buttonData.label) {
      error('Button label required', 'Please enter a button label');
      return;
    }

    if (!buttonData.action?.type) {
      error('Action not configured', 'Please configure an action');
      return;
    }

    // Action-specific validation
    if (buttonData.action.type === 'call-api' && !buttonData.action.apiUrl) {
      error('API URL required', 'Please enter an API URL');
      return;
    }

    if (buttonData.action.type === 'run-workflow' && !buttonData.action.workflowId) {
      error('Workflow not selected', 'Please select a workflow');
      return;
    }

    if (buttonData.action.type === 'update-fields' && (!buttonData.action.fieldUpdates || buttonData.action.fieldUpdates.length === 0)) {
      error('No field updates', 'Please add at least one field update');
      return;
    }

    if (buttonData.action.type === 'navigate' && !buttonData.action.navigateUrl) {
      error('Navigation URL required', 'Please enter a navigation URL');
      return;
    }

    if (buttonData.action.type === 'javascript' && !buttonData.action.javascriptCode) {
      error('JavaScript code required', 'Please enter JavaScript code');
      return;
    }

    try {
      if (initialButton) {
        await updateButtonMutation.mutateAsync({
          buttonId: initialButton.id,
          updates: buttonData
        });
      } else {
        await createButtonMutation.mutateAsync(buttonData as Omit<ButtonAction, 'id' | 'createdBy' | 'createdDate' | 'lastModified' | 'lastModifiedBy'>);
      }
      success('Button/action saved successfully!');
      setHasChanges(false);
      onClose();
    } catch (err: any) {
      error('Failed to save button/action', err.message || 'Please try again');
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const locationOptions: Array<{ value: ButtonLocation; label: string; description: string }> = [
    { value: 'detail-header', label: 'Detail Page Header', description: 'Top of the record page' },
    { value: 'detail-section', label: 'Detail Page Section', description: 'Within a specific section' },
    { value: 'detail-footer', label: 'Detail Page Footer', description: 'Bottom of the record page' },
    { value: 'list-row', label: 'List View Row', description: 'On each row in the list' },
    { value: 'list-bulk', label: 'List View Bulk', description: 'Bulk action for selected records' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {initialButton ? 'Edit' : 'Create'} Button/Action
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {objectLabel} • Add custom functionality
            </p>
          </div>

          <div className="flex items-center gap-3">
            {hasChanges && (
              <div className="flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-400">
                <AlertCircle className="w-4 h-4" />
                Unsaved changes
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={!hasChanges || createButtonMutation.isPending || updateButtonMutation.isPending}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {createButtonMutation.isPending || updateButtonMutation.isPending ? 'Saving...' : 'Save'}
            </button>

            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-900 dark:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-3xl mx-auto space-y-6">
            {/* Basic Info */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Basic Information
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Button Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={buttonData.name}
                    onChange={(e) => {
                      setButtonData(prev => ({ ...prev, name: e.target.value }));
                      setHasChanges(true);
                    }}
                    placeholder="e.g., Send Welcome Email"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Type
                    </label>
                    <select
                      value={buttonData.type}
                      onChange={(e) => {
                        setButtonData(prev => ({ ...prev, type: e.target.value as ButtonType }));
                        setHasChanges(true);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="button">Custom Button</option>
                      <option value="quick-action">Quick Action</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Location
                    </label>
                    <select
                      value={buttonData.location}
                      onChange={(e) => {
                        setButtonData(prev => ({ ...prev, location: e.target.value as ButtonLocation }));
                        setHasChanges(true);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      {locationOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Button Label <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={buttonData.label}
                      onChange={(e) => {
                        setButtonData(prev => ({ ...prev, label: e.target.value }));
                        setHasChanges(true);
                      }}
                      placeholder="e.g., Send Email"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Color
                    </label>
                    <select
                      value={buttonData.color}
                      onChange={(e) => {
                        setButtonData(prev => ({ ...prev, color: e.target.value as any }));
                        setHasChanges(true);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="purple">Purple</option>
                      <option value="blue">Blue</option>
                      <option value="green">Green</option>
                      <option value="red">Red</option>
                      <option value="yellow">Yellow</option>
                      <option value="gray">Gray</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={buttonData.isActive}
                      onChange={(e) => {
                        setButtonData(prev => ({ ...prev, isActive: e.target.checked }));
                        setHasChanges(true);
                      }}
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Active
                    </span>
                  </label>
                </div>

                {/* Button Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Preview
                  </label>
                  <button
                    disabled
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                      buttonData.color === 'purple' ? 'bg-purple-600 text-white' :
                      buttonData.color === 'blue' ? 'bg-blue-600 text-white' :
                      buttonData.color === 'green' ? 'bg-green-600 text-white' :
                      buttonData.color === 'red' ? 'bg-red-600 text-white' :
                      buttonData.color === 'yellow' ? 'bg-yellow-600 text-white' :
                      'bg-gray-600 text-white'
                    } opacity-75 cursor-not-allowed`}
                  >
                    {buttonData.label || 'Button Label'}
                  </button>
                </div>
              </div>
            </div>

            {/* Action Configuration */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Action Configuration
              </h3>

              <ActionConfigurator
                action={buttonData.action!}
                availableFields={allFields}
                onChange={(action) => {
                  setButtonData(prev => ({ ...prev, action }));
                  setHasChanges(true);
                }}
              />
            </div>

            {/* Success/Error Messages */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Feedback Messages
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Success Message
                  </label>
                  <input
                    type="text"
                    value={buttonData.successMessage}
                    onChange={(e) => {
                      setButtonData(prev => ({ ...prev, successMessage: e.target.value }));
                      setHasChanges(true);
                    }}
                    placeholder="Action completed successfully!"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Error Message
                  </label>
                  <input
                    type="text"
                    value={buttonData.errorMessage}
                    onChange={(e) => {
                      setButtonData(prev => ({ ...prev, errorMessage: e.target.value }));
                      setHasChanges(true);
                    }}
                    placeholder="Action failed. Please try again."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Visibility Rules */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Visibility Rules
              </h3>

              <VisibilityRulesBuilder
                rules={buttonData.visibilityRules!}
                logic={buttonData.visibilityLogic!}
                availableFields={allFields}
                onChange={(rules, logic) => {
                  setButtonData(prev => ({ ...prev, visibilityRules: rules, visibilityLogic: logic }));
                  setHasChanges(true);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
